const root = document.documentElement;
let banner;
let preferences;
let frame;
let occupied = 0;
let overlay = false;

function publishGeometry() {
  const bannerBounds = banner?.getBoundingClientRect();
  const bannerVisible = Boolean(bannerBounds?.height && banner.getClientRects().length);
  const nativePreferencesVisible = preferencesAreVisible();
  const fallback = document.querySelector('privacy-preferences');
  if (nativePreferencesVisible && fallback?.isOpen) {
    fallback.close(false);
    document.getElementById('shopify-pc__prefs__dialog')?.querySelector('button, input, a[href], [tabindex]')?.focus({ preventScroll: true });
  }
  const preferencesVisible = nativePreferencesVisible || Boolean(fallback?.isOpen);
  const nextOccupied = bannerVisible ? Math.ceil(innerHeight - bannerBounds.top) : 0;
  const nextOverlay = bannerVisible || preferencesVisible;
  if (nextOccupied === occupied && nextOverlay === overlay) return;
  occupied = nextOccupied;
  overlay = nextOverlay;
  root.style.setProperty('--privacy-occupied', `${occupied}px`);
  root.toggleAttribute('data-privacy-overlay', overlay);
  document.dispatchEvent(new CustomEvent('privacy:geometry'));
}

function scheduleGeometry() {
  cancelAnimationFrame(frame);
  frame = requestAnimationFrame(publishGeometry);
}

const sizeObserver = new ResizeObserver(scheduleGeometry);
const visibilityObserver = new MutationObserver(scheduleGeometry);

function observePrivacy() {
  for (const control of document.querySelectorAll('[data-privacy-preferences]')) {
    control.hidden = false;
  }
  const nextBanner = document.getElementById('shopify-pc__banner');
  const nextPreferences = document.getElementById('shopify-pc__prefs');
  if (banner !== nextBanner || preferences !== nextPreferences) {
    sizeObserver.disconnect();
    visibilityObserver.disconnect();
    banner = nextBanner;
    preferences = nextPreferences;
    for (const element of [banner, preferences]) {
      if (!element) continue;
      sizeObserver.observe(element);
      visibilityObserver.observe(element, {
        attributes: true,
        attributeFilter: ['style', 'class', 'hidden'],
      });
    }
  }
  scheduleGeometry();
}

// Shopify inserts and removes these direct body children outside the theme lifecycle.
const insertionObserver = new MutationObserver(observePrivacy);

function connect() {
  insertionObserver.observe(document.body, { childList: true });
  observePrivacy();
  window.addEventListener('resize', scheduleGeometry);
}

window.addEventListener('pagehide', () => {
  insertionObserver.disconnect();
  sizeObserver.disconnect();
  visibilityObserver.disconnect();
  window.removeEventListener('resize', scheduleGeometry);
  cancelAnimationFrame(frame);
  banner = null;
  preferences = null;
});
window.addEventListener('pageshow', connect);
connect();

let bannerLoader;
let consentLoader;

function loadConsentApi() {
  if (typeof window.Shopify?.customerPrivacy?.setTrackingConsent === 'function') {
    return Promise.resolve();
  }
  if (consentLoader) return consentLoader;
  consentLoader = new Promise((resolve, reject) => {
    const timeout = setTimeout(() => finish(new Error('Customer Privacy API load timed out')), 10000);
    function finish(error) {
      clearTimeout(timeout);
      if (error) {
        reject(error);
      } else {
        resolve();
      }
    }
    if (typeof window.Shopify?.loadFeatures !== 'function') {
      finish(new Error('Shopify feature loader unavailable'));
      return;
    }
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.2' }], error => {
      finish(error || (typeof window.Shopify?.customerPrivacy?.setTrackingConsent !== 'function' ? new Error('Customer Privacy API unavailable') : null));
    });
  }).catch(error => {
    consentLoader = null;
    throw error;
  });
  return consentLoader;
}

function loadPrivacyBanner() {
  if (typeof window.privacyBanner?.showPreferences === 'function') {
    return Promise.resolve(window.privacyBanner);
  }
  if (bannerLoader) return bannerLoader;
  // Shopify's documented CDN can recover a failed storefront-proxied asset.
  const injected = document.querySelector('script[src*="/shopifycloud/privacy-banner/storefront-banner.js"]');
  if (!injected) return Promise.reject(new Error('Shopify privacy banner is not configured'));
  const canonicalSource = 'https://cdn.shopify.com/shopifycloud/privacy-banner/storefront-banner.js';
  const sources = [canonicalSource];
  try {
    const source = new URL(injected.src, location.href);
    const storefrontSource = source.origin === location.origin && source.pathname === '/cdn/shopifycloud/privacy-banner/storefront-banner.js';
    const shopifySource = source.origin === 'https://cdn.shopify.com' && source.pathname === '/shopifycloud/privacy-banner/storefront-banner.js';
    if (!source.username && !source.password && (storefrontSource || shopifySource) && source.href !== canonicalSource) sources.push(source.href);
  } catch {}
  bannerLoader = new Promise((resolve, reject) => {
    let script;
    const timeout = setTimeout(() => finish(new Error('Privacy banner load timed out')), 10000);
    function detach() {
      if (!script) return;
      script.onload = null;
      script.onerror = null;
      script.remove();
    }
    function finish(error) {
      clearTimeout(timeout);
      script.onload = null;
      script.onerror = null;
      if (error) {
        detach();
        bannerLoader = null;
        reject(error);
      } else {
        resolve(window.privacyBanner);
      }
    }
    function loadNext() {
      detach();
      const source = sources.shift();
      if (!source) {
        finish(new Error('Privacy banner could not load'));
        return;
      }
      script = document.createElement('script');
      script.src = source;
      script.async = true;
      script.onload = () => typeof window.privacyBanner?.showPreferences === 'function' ? finish() : loadNext();
      script.onerror = loadNext;
      document.head.append(script);
    }
    loadNext();
  });
  return bannerLoader;
}

function preferencesAreVisible() {
  const dialog = document.getElementById('shopify-pc__prefs__dialog');
  const bounds = dialog?.getBoundingClientRect();
  return Boolean(bounds?.width && bounds.height && dialog.getClientRects().length);
}

let preferencesOpening;

function openPrivacyPreferences(trigger) {
  if (preferencesAreVisible()) return Promise.resolve();
  if (document.querySelector?.('privacy-preferences')?.isOpen) return Promise.resolve();
  if (preferencesOpening) return preferencesOpening;
  let closed = false;
  const cancelOpening = () => { closed = true; };
  window.addEventListener('pagehide', cancelOpening, { once: true });
  preferencesOpening = (async () => {
    await loadConsentApi();
    if (closed || trigger && !trigger.isConnected) throw new Error('Privacy preferences opening cancelled');
    try {
      const privacyBanner = await loadPrivacyBanner();
      if (closed || trigger && !trigger.isConnected) throw new Error('Privacy preferences opening cancelled');
      if (preferencesAreVisible()) return;
      // Shopify's promise can resolve before its native dialog has rendered.
      await new Promise((resolve, reject) => {
        const observer = new MutationObserver(checkVisibility);
        const timeout = setTimeout(() => finish(new Error('Privacy preferences did not open')), 12000);
        function finish(error) {
          observer.disconnect();
          clearTimeout(timeout);
          window.removeEventListener('pagehide', cancel);
          error ? reject(error) : resolve();
        }
        function checkVisibility() {
          if (preferencesAreVisible()) finish();
        }
        function cancel() {
          finish(new Error('Page closed before privacy preferences opened'));
        }
        observer.observe(document.body, {
          childList: true, subtree: true, attributes: true,
          attributeFilter: ['style', 'class', 'hidden'],
        });
        window.addEventListener('pagehide', cancel, { once: true });
        try {
          Promise.resolve(privacyBanner.showPreferences()).catch(finish);
          checkVisibility();
        } catch (error) {
          finish(error);
        }
      });
    } catch (error) {
      if (closed || trigger && !trigger.isConnected) throw error;
      if (preferencesAreVisible()) return;
      const fallback = document.querySelector?.('privacy-preferences');
      if (!fallback?.dataset.scriptUrl || !fallback.isConnected) throw error;
      await import(fallback.dataset.scriptUrl);
      if (closed || !fallback.isConnected || trigger && !trigger.isConnected) throw new Error('Privacy preferences opening cancelled');
      if (preferencesAreVisible()) return;
      fallback.openPreferences(window.Shopify.customerPrivacy, trigger);
    }
  })().finally(() => {
    window.removeEventListener('pagehide', cancelOpening);
    preferencesOpening = null;
  });
  return preferencesOpening;
}

document.addEventListener('click', async (event) => {
  const control = event.target.closest('[data-privacy-preferences]');
  if (!control) return;
  control.disabled = true;
  control.setAttribute('aria-busy', 'true');
  const loadingMarker = document.createTextNode('…');
  control.append(loadingMarker);
  const error = control.parentElement.querySelector('[data-privacy-error]');
  if (error) error.hidden = true;
  try {
    await openPrivacyPreferences(control);
  } catch {
    if (error) error.hidden = false;
  } finally {
    control.disabled = false;
    control.removeAttribute('aria-busy');
    loadingMarker.remove();
  }
});
document.addEventListener('shopify:section:load', observePrivacy);
document.addEventListener('privacy:preferences-change', scheduleGeometry);
